import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
} from "@mui/material";
import type { IScannerControls } from "@zxing/browser";

export interface BarcodeScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onScan: (result: { text: string; imageBase64: string }) => void;
}

function captureFrameAsBase64Png(video: HTMLVideoElement): string {
  const width = video.videoWidth;
  const height = video.videoHeight;

  // If the video isn't ready yet, drawing to canvas can produce a blank/transparent image.
  if (!width || !height) return "";

  // Downscale to keep the base64 reasonably sized for PDF rendering.
  // Large desktop camera frames can produce huge base64 strings and result in blank images in React-PDF.
  const maxDimension = 1280;
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return "";

  ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
  return canvas.toDataURL("image/png");
}

const captureFrameWithRetries = (
  videoEl: HTMLVideoElement,
  attemptsLeft: number,
  onCaptured: (imageBase64: string) => void
) => {
  const imageBase64 = captureFrameAsBase64Png(videoEl);
  if (imageBase64 || attemptsLeft <= 0) {
    onCaptured(imageBase64);
    return;
  }

  requestAnimationFrame(() => {
    captureFrameWithRetries(videoEl, attemptsLeft - 1, onCaptured);
  });
};

// Helper: Check if scanning environment is valid
const isScanningEnvironmentValid = (): boolean => {
  return globalThis.window !== undefined && !!navigator?.mediaDevices?.getUserMedia;
};

// Helper: Stop scanner controls safely
const stopScannerControls = (controls: IScannerControls | null) => {
  try {
    controls?.stop();
  } catch {
    // ignore
  }
};

// Helper: Handle scan result
const handleScanResult = (
  result: any,
  videoEl: HTMLVideoElement,
  controls: IScannerControls,
  controlsRef: React.MutableRefObject<IScannerControls | null>,
  scanCompleteRef: React.MutableRefObject<boolean>,
  onScan: (result: { text: string; imageBase64: string }) => void,
  onClose: () => void,
  isCancelled: boolean
): void => { /** NOSONAR */
  if (isCancelled || !result || scanCompleteRef.current) return;

  scanCompleteRef.current = true;

  const text = typeof result?.getText === "function" ? result.getText() : result.toString();

  const finalize = (imageBase64: string) => {
    stopScannerControls(controls);
    controlsRef.current = null;
    onScan({ text, imageBase64 });
    onClose();
  };

  // Capture BEFORE stopping controls. On some desktops, stopping can end the camera stream
  // immediately, resulting in a blank image.
  captureFrameWithRetries(videoEl, 10, finalize);
};

// Helper: Start barcode scanner
const startBarcodeScanner = async (
  videoEl: HTMLVideoElement,
  controlsRef: React.MutableRefObject<IScannerControls | null>,
  scanCompleteRef: React.MutableRefObject<boolean>,
  onScan: (result: { text: string; imageBase64: string }) => void,
  onClose: () => void,
  setIsStarting: (value: boolean) => void,
  setError: (error: string | null) => void
): Promise<() => void> => { /** NOSONAR */
  let isCancelled = false;
  let scannerControls: IScannerControls | null = null;
  let pendingResult: any = null;

  setIsStarting(true);
  setError(null);
  
  try {
    const { BrowserMultiFormatReader } = await import("@zxing/browser");
    const reader = new BrowserMultiFormatReader();

    const controls = await reader.decodeFromVideoDevice(
      undefined,
      videoEl,
      (result) => {
        if (isCancelled || !result || scanCompleteRef.current) return;

        // `decodeFromVideoDevice` can invoke callbacks before its Promise resolves.
        // If that happens, `controlsRef.current` (and `scannerControls`) aren't set yet,
        // and we'd drop a valid scan result.
        if (!scannerControls) {
          pendingResult = result;
          return;
        }

        handleScanResult(
          result,
          videoEl,
          scannerControls,
          controlsRef,
          scanCompleteRef,
          onScan,
          onClose,
          isCancelled
        );
      }
    );

    scannerControls = controls;
    controlsRef.current = controls;

    if (pendingResult && !scanCompleteRef.current && !isCancelled) {
      handleScanResult(
        pendingResult,
        videoEl,
        controls,
        controlsRef,
        scanCompleteRef,
        onScan,
        onClose,
        isCancelled
      );
      pendingResult = null;
    }

    if (isCancelled) {
      stopScannerControls(controls);
      return () => {};
    }
  } catch (error: any) {
    const errorMessage = error?.message || "Failed to start camera";
    if (errorMessage.includes("Permission denied") || errorMessage.includes("NotAllowedError")) {
      setError("Camera permission denied. Please allow camera access in your browser settings.");
    } else if (errorMessage.includes("NotFoundError") || errorMessage.includes("not found")) {
      setError("No camera found. Please connect a camera and try again.");
    } else {
      setError(`Failed to start camera: ${errorMessage}`);
    }
  } finally {
    if (!isCancelled) setIsStarting(false);
  }

  return () => {
    isCancelled = true;
    setIsStarting(false);
    stopScannerControls(scannerControls ?? controlsRef.current);
    controlsRef.current = null;
    scannerControls = null;
    pendingResult = null;
  };
};

const BarcodeScannerDialog: React.FC<BarcodeScannerDialogProps> = ({
  open,
  onClose,
  onScan,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const scanCompleteRef = useRef(false);
  const [isStarting, setIsStarting] = useState(false);
  const [dialogEntered, setDialogEntered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    scanCompleteRef.current = false;
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || !dialogEntered) return;

    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (!isScanningEnvironmentValid()) {
      setError("Camera not supported in this environment.");
      return;
    }

    const cleanup = startBarcodeScanner(
      videoEl,
      controlsRef,
      scanCompleteRef,
      onScan,
      onClose,
      setIsStarting,
      setError
    );

    return () => {
      cleanup.then((fn) => fn());
    };
  }, [open, dialogEntered, onClose, onScan]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{
        onEntered: () => setDialogEntered(true),
        onExit: () => setDialogEntered(false),
      }}
    >
      <DialogTitle>Scan Barcode</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <video
            ref={videoRef}
            data-testid="barcode-scanner-video"
            style={{ 
              width: "100%", 
              borderRadius: 4,
              display: error ? "none" : "block"
            }}
            muted
            playsInline
            autoPlay
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isStarting}>
          {error ? "Close" : "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeScannerDialog;
